/**
 * Instrument classification that the source files cannot supply themselves.
 *
 * Dependency-free — not one import — so `pnpm vendor:shared` can copy it into
 * the Edge Functions, which bundle from `supabase/functions` alone. See D-18.
 * `asset-class.test.ts` asserts the union below still matches `ASSET_CLASSES`
 * in `types/enums.ts`, which in turn mirrors the `public.asset_class` enum.
 */

export const ASSET_CLASS_VALUES = [
  'equity',
  'bond',
  'cash',
  'commodity',
  'crypto',
  'other',
] as const;

export type KnownAssetClass = (typeof ASSET_CLASS_VALUES)[number];

/**
 * ISINs whose asset class no export states correctly.
 *
 * Lightyear parks uninvested cash in BlackRock's money-market funds and
 * reports them as ordinary holdings — constant 1.00 price, a monthly
 * `Distribution` it immediately reinvests. Nothing in the CSV says "this is
 * cash", so left alone they would sit on the equity side of the equity/bond
 * split and quietly overstate it. Confirmed with the account holder — O-11.
 */
export const ASSET_CLASS_BY_ISIN: Readonly<Record<string, KnownAssetClass>> = {
  IE000GWTNRJ7: 'cash', // BlackRock ICS Euro Liquidity — Lightyear "BRICEKSP"
  IE00B44BQ083: 'cash', // BlackRock ICS US Dollar Liquidity — Lightyear "ICSUSSDP"
};

/**
 * The asset class to give an instrument the first time we meet it.
 *
 * Apply this only on insert. A class the user has corrected by hand has to
 * survive every later sync, so nothing may write it on update.
 */
export function assetClassFor(isin: string | null, fallback: KnownAssetClass): KnownAssetClass {
  if (isin === null) return fallback;
  return ASSET_CLASS_BY_ISIN[isin.trim().toUpperCase()] ?? fallback;
}
